import React, { useEffect, useState } from 'react';
import * as jsxRuntime from 'react/jsx-runtime';

export interface FeedbackWidgetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideLauncher?: boolean;
  sessionId: string;
  appId: string;
  customerName: string;
  environment: string;
  feedbackUrl?: string;
  backendUrl?: string;
  userName?: string;
  userEmail?: string;
  feedbackType?: 'neutral' | 'positive' | 'negative';
  feedbackMinChars?: number;
  feedbackMaxChars?: number;
  extras?: Record<string, unknown>;
  getExtras?: (input: {
    feedbackType: 'neutral' | 'positive' | 'negative';
    message: string;
  }) => Record<string, unknown> | Promise<Record<string, unknown> | undefined> | undefined;
}

interface ManifestVariant {
  url: string;
  integrity?: string;
}

interface WidgetManifest {
  variants?: {
    react18?: ManifestVariant;
  };
}

type FeedbackWidgetComponent = React.FC<FeedbackWidgetProps>;

declare global {
  interface Window {
    FFS_MANIFEST_URL?: string;
    FFS_BACKEND_URL?: string;
    FusionFlowFeedbackWidget?:
      | {
          default?: FeedbackWidgetComponent;
        }
      | FeedbackWidgetComponent;
  }
}

const DEFAULT_MANIFEST_URL = 'https://feedback-cdn.fusionflow.dev:7788/manifest.json';
const MANIFEST_URL =
  (import.meta.env.VITE_FFS_MANIFEST_URL as string | undefined)?.trim() || DEFAULT_MANIFEST_URL;

let loadPromise: Promise<FeedbackWidgetComponent> | null = null;

function getWidgetFromGlobal(): FeedbackWidgetComponent | null {
  const widget = window.FusionFlowFeedbackWidget;
  if (!widget) return null;
  if (typeof widget === 'function') return widget;
  return widget.default ?? null;
}

function installReactRequireShim(): void {
  const requireWindow = window as Window & { require?: (id: string) => unknown };
  const previousRequire = requireWindow.require;

  requireWindow.require = (id: string) => {
    if (id === 'react') return React;
    if (id === 'react/jsx-runtime') return jsxRuntime;
    if (previousRequire) return previousRequire(id);
    throw new Error(`Unsupported CDN widget dependency: ${id}`);
  };
}

async function fetchReact18Variant(): Promise<ManifestVariant> {
  const manifestUrl = new URL(MANIFEST_URL);
  manifestUrl.searchParams.set('t', String(Math.floor(Date.now() / 3_600_000)));

  const response = await fetch(manifestUrl.toString(), { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Feedback widget manifest returned HTTP ${response.status}`);
  }

  const manifest = (await response.json()) as WidgetManifest;
  const variant = manifest.variants?.react18;
  if (!variant?.url) {
    throw new Error('Feedback widget manifest does not include a react18 variant');
  }

  return variant;
}

function loadScript(variant: ManifestVariant): Promise<void> {
  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[data-ffs-feedback-widget="react18"][src="${variant.url}"]`,
  );
  if (existingScript && getWidgetFromGlobal()) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const script = existingScript ?? document.createElement('script');
    script.src = variant.url;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.dataset.ffsFeedbackWidget = 'react18';
    if (variant.integrity) {
      script.integrity = variant.integrity;
    }
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error(`Failed to load feedback widget bundle: ${variant.url}`));

    if (!existingScript) {
      document.head.appendChild(script);
    }
  });
}

async function loadFeedbackWidget(): Promise<FeedbackWidgetComponent> {
  const existing = getWidgetFromGlobal();
  if (existing) return existing;

  installReactRequireShim();
  window.FFS_MANIFEST_URL = MANIFEST_URL;

  const variant = await fetchReact18Variant();
  await loadScript(variant);

  const widget = getWidgetFromGlobal();
  if (!widget) {
    throw new Error('Feedback widget bundle loaded without exposing FusionFlowFeedbackWidget');
  }

  return widget;
}

export const CdnFeedbackWidget: React.FC<FeedbackWidgetProps> = (props) => {
  const [Widget, setWidget] = useState<FeedbackWidgetComponent | null>(() => getWidgetFromGlobal());

  useEffect(() => {
    let mounted = true;

    loadPromise ??= loadFeedbackWidget();
    loadPromise
      .then((loadedWidget) => {
        if (mounted) setWidget(() => loadedWidget);
      })
      .catch((error: unknown) => {
        console.warn('[sales-order-automation] feedback widget CDN load failed', error);
      });

    return () => {
      mounted = false;
    };
  }, []);

  if (!Widget) return null;

  return <Widget {...props} />;
};
